import React from "react";
import { Link } from "react-router-dom";
import { AppShell } from "../../components/Sidebar";
import { useAuth } from "../../context/AuthContext";
import { User, Mail, Phone, School, KeyRound, Pencil, Lock } from "lucide-react";

const initials = (name = "U") => name.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

export default function Settings() {
  const { user } = useAuth();
  if (!user) return null;

  return (
    <AppShell role="teacher">
      <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Settings</h1>
      <p className="mt-1 text-slate-500">Manage your profile and account security.</p>

      {/* Header card */}
      <div className="mt-7 rounded-2xl border border-slate-100 bg-white p-6 shadow-card">
        <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-indigo-100 text-3xl font-bold text-indigo-700">
            {initials(user.full_name)}
          </div>
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-slate-900">{user.full_name}</h2>
            <p className="text-sm text-slate-500">{user.email} • {user.mobile}</p>
            <p className="mt-1 text-sm text-slate-500"><School className="mr-1 inline" size={14} /> {user.school_name} • <span className="font-semibold text-indigo-600">Teacher</span></p>
          </div>
        </div>
      </div>

      {/* Profile Information */}
      <div className="mt-6 rounded-2xl border border-slate-100 bg-white p-6 shadow-card">
        <div className="mb-5 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-slate-900">Profile Information</h3>
            <p className="text-sm text-slate-500">Manage your personal details.</p>
          </div>
          <Link to="/teacher/settings/edit-profile" data-testid="edit-profile-btn" className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow hover:bg-indigo-700">
            <Pencil size={14} /> Edit Profile
          </Link>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <FieldRow icon={User} label="Full Name" value={user.full_name} />
          <FieldRow icon={Mail} label="Email Address" value={user.email}
            action={<Link to="/teacher/settings/change-email" data-testid="change-email-btn" className="text-sm font-semibold text-indigo-600 hover:underline">Change</Link>} />
          <FieldRow icon={Phone} label="Mobile Number" value={user.mobile}
            action={<Link to="/teacher/settings/change-mobile" data-testid="change-mobile-btn" className="text-sm font-semibold text-indigo-600 hover:underline">Change</Link>} />
        </div>
      </div>

      {/* Account & Security */}
      <div className="mt-6 rounded-2xl border border-slate-100 bg-white p-6 shadow-card">
        <h3 className="text-lg font-bold text-slate-900">Account &amp; Security</h3>
        <p className="text-sm text-slate-500">Keep your account safe.</p>

        <div className="mt-5 flex flex-col items-start justify-between gap-4 rounded-xl bg-slate-50 p-5 sm:flex-row sm:items-center">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-100 text-violet-600"><Lock size={18} /></div>
            <div>
              <p className="text-sm font-semibold text-slate-700">Password</p>
              <p className="text-xs text-slate-500">●●●●●●●●●● — Last changed: {user.password_last_changed || "—"}</p>
            </div>
          </div>
          <Link to="/teacher/settings/change-password" data-testid="change-password-btn" className="inline-flex items-center gap-2 rounded-xl border border-indigo-200 bg-white px-4 py-2.5 text-sm font-semibold text-indigo-600 hover:bg-indigo-50">
            <KeyRound size={14} /> Change Password
          </Link>
        </div>
      </div>
    </AppShell>
  );
}

const FieldRow = ({ icon: Icon, label, value, action }) => (
  <div className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3">
    <div className="flex items-center gap-3">
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-white text-slate-500 shadow-sm"><Icon size={16} /></div>
      <div>
        <p className="text-xs text-slate-500">{label}</p>
        <p className="text-sm font-semibold text-slate-900">{value}</p>
      </div>
    </div>
    {action}
  </div>
);
